import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Calendar, ArrowLeft, Phone } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/slug/${slug}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar onBookNow={() => {}} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <h1 className="text-4xl font-bold mb-4">Blog Post Not Found</h1>
          <Link to="/blogs" className="text-[#d4af37] hover:underline">
            ← Back to Blogs
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar onBookNow={() => {}} />
      
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/blogs" 
            className="inline-flex items-center text-[#d4af37] hover:text-[#b8941f] mb-8 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blogs
          </Link>

          {/* Blog Header */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              {blog.title}
            </h1>
            
            <div className="flex items-center text-gray-400 space-x-6">
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-[#d4af37]" />
                {formatDate(blog.created_at)}
              </div>
              <div>By {blog.author}</div>
            </div>
          </header>

          {/* Blog Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <ReactMarkdown
              components={{
                h2: ({node, ...props}) => <h2 className="text-3xl font-bold mt-12 mb-6 text-[#d4af37]" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-2xl font-bold mt-8 mb-4 text-white" {...props} />,
                p: ({node, ...props}) => <p className="text-gray-300 mb-6 leading-relaxed" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-6 text-gray-300" {...props} />,
                li: ({node, ...props}) => <li className="leading-relaxed" {...props} />,
                strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
              }}
            >
              {blog.content}
            </ReactMarkdown>
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4 text-[#d4af37]">
              Need Our Services?
            </h3>
            <p className="text-gray-300 mb-6">
              Get in touch with YYC Pick-Up for all your delivery and hauling needs in Calgary
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:4038014638"
                className="inline-flex items-center justify-center bg-[#d4af37] text-black px-8 py-3 rounded-lg font-bold hover:bg-[#b8941f] transition"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call (403) 801-4638
              </a>
              <Link 
                to="/#contact"
                className="inline-flex items-center justify-center bg-transparent border-2 border-[#d4af37] text-[#d4af37] px-8 py-3 rounded-lg font-bold hover:bg-[#d4af37] hover:text-black transition"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
